import { getRecommendedPlacementValue } from "./placementRules";

const textOf = (type) => `${type.code || ""} ${type.name || ""}`.toLowerCase();
const isAllEventType = (type) => /allevent|all event/.test(textOf(type));
const hasHdcp = (type) =>
  textOf(type).includes("hdcp") || textOf(type).includes("handicap");
const has = (type, ...parts) => parts.every((part) => textOf(type).includes(part));

export const matchCalculationType = (types, kind) => {
  const list = types || [];
  const regular = list.filter((type) => !isAllEventType(type));

  switch (kind) {
    case "highGameHdcp":
      return regular.find(
        (type) => has(type, "high") && has(type, "game") && hasHdcp(type) && !has(type, "series")
      );
    case "highGameScratch":
      return regular.find(
        (type) =>
          has(type, "high") &&
          has(type, "game") &&
          has(type, "scratch") &&
          !has(type, "series")
      );
    case "highSeriesHdcp":
      return regular.find(
        (type) => has(type, "high") && has(type, "series") && hasHdcp(type)
      );
    case "highSeriesScratch":
      return regular.find(
        (type) => has(type, "high") && has(type, "series") && has(type, "scratch")
      );
    case "handicapSeries":
      return (
        regular.find((type) => type.code === "handicap") ||
        regular.find(
          (type) => has(type, "high") && has(type, "series") && hasHdcp(type)
        ) ||
        regular.find((type) => hasHdcp(type) && !has(type, "game"))
      );
    case "allEventHdcp":
      return (
        list.find((type) => type.code === "allEventHdcp") ||
        list.find((type) => isAllEventType(type) && hasHdcp(type))
      );
    case "allEventScratch":
      return (
        list.find((type) => type.code === "allEventScratch") ||
        list.find((type) => isAllEventType(type) && has(type, "scratch"))
      );
    default:
      return null;
  }
};

export const EVENT_TEMPLATES = [
  {
    id: "singles",
    group: "primary",
    label: "Singles",
    eventName: "Singles",
    eventAbbreviation: "SGL",
    isPrimary: true,
    bowlerQuantity: 1,
    gamesPerEvent: 3,
    calc: "handicapSeries",
  },
  {
    id: "doubles",
    group: "primary",
    label: "Doubles",
    eventName: "Doubles",
    eventAbbreviation: "DBL",
    isPrimary: true,
    bowlerQuantity: 2,
    gamesPerEvent: 3,
    calc: "handicapSeries",
  },
  {
    id: "team",
    group: "primary",
    label: "Team",
    eventName: "Team",
    eventAbbreviation: "TM",
    isPrimary: true,
    bowlerQuantity: 4,
    gamesPerEvent: 3,
    calc: "handicapSeries",
  },
  {
    id: "highGameHdcp",
    group: "sidepot",
    label: "High game handicap",
    eventName: "High Game Handicap",
    eventAbbreviation: "HGH",
    isPrimary: false,
    bowlerQuantity: 1,
    gamesPerEvent: null,
    calc: "highGameHdcp",
  },
  {
    id: "highGameScratch",
    group: "sidepot",
    label: "High game scratch",
    eventName: "High Game Scratch",
    eventAbbreviation: "HGS",
    isPrimary: false,
    bowlerQuantity: 1,
    gamesPerEvent: null,
    calc: "highGameScratch",
  },
  {
    id: "highSeriesHdcp",
    group: "sidepot",
    label: "High series handicap",
    eventName: "High Series Handicap",
    eventAbbreviation: "HSH",
    isPrimary: false,
    bowlerQuantity: 1,
    gamesPerEvent: null,
    calc: "highSeriesHdcp",
  },
  {
    id: "highSeriesScratch",
    group: "sidepot",
    label: "High series scratch",
    eventName: "High Series Scratch",
    eventAbbreviation: "HSS",
    isPrimary: false,
    bowlerQuantity: 1,
    gamesPerEvent: null,
    calc: "highSeriesScratch",
  },
  {
    id: "allEventHdcp",
    group: "sidepot",
    isAllEvent: true,
    label: "All event handicap",
    eventName: "All Event Handicap",
    eventAbbreviation: "AEH",
    isPrimary: false,
    bowlerQuantity: 1,
    gamesPerEvent: null,
    calc: "allEventHdcp",
  },
  {
    id: "allEventScratch",
    group: "sidepot",
    isAllEvent: true,
    label: "All event scratch",
    eventName: "All Event Scratch",
    eventAbbreviation: "AES",
    isPrimary: false,
    bowlerQuantity: 1,
    gamesPerEvent: null,
    calc: "allEventScratch",
  },
];

export const primaryEventType = (event) => {
  if (!event?.isPrimary || event.isTemplate) return null;
  const quantity = Number(event.bowlerQuantity);
  if (quantity === 1) return "singles";
  if (quantity === 2) return "doubles";
  return "team";
};

export const uniquePrimaryTypeCount = (events) =>
  new Set((events || []).map(primaryEventType).filter(Boolean)).size;

export const upcomingPrimaryTypeCount = (events, selectedPrimaryTemplates = []) => {
  const types = new Set((events || []).map(primaryEventType).filter(Boolean));
  selectedPrimaryTemplates.forEach((template) => {
    if (template?.group === "primary") types.add(template.id);
  });
  return types.size;
};

export const isAllEventTemplate = (template) =>
  Boolean(template?.isAllEvent) ||
  template?.calc === "allEventHdcp" ||
  template?.calc === "allEventScratch";

export const isPrimaryEvent = (event) =>
  Boolean(event?.isPrimary) && !event.isTemplate;

export const isPerSquadSidePot = (event) => Boolean(event?.isTemplate);

export const isSidePotEvent = (event) =>
  !event?.isPrimary && !event.isTemplate;

export const templateAlreadyAdded = (template, events) => {
  const list = events || [];
  if (template.group === "primary") {
    return list.some((event) => primaryEventType(event) === template.id);
  }
  if (template.calc === "allEventHdcp") {
    return list.some(
      (event) =>
        event.calculationTypeCode === "allEventHdcp" ||
        (event.eventName || "").toLowerCase() === "all event handicap"
    );
  }
  if (template.calc === "allEventScratch") {
    return list.some(
      (event) =>
        event.calculationTypeCode === "allEventScratch" ||
        (event.eventName || "").toLowerCase() === "all event scratch"
    );
  }
  return list.some(
    (event) =>
      !event.isTemplate &&
      (event.eventName || "").toLowerCase() === template.eventName.toLowerCase()
  );
};

export const buildEventFromTemplate = (
  template,
  calculationTypeId,
  events = []
) => {
  const quantity = template.bowlerQuantity;
  const payload = {
    eventName: template.eventName,
    eventAbbreviation: template.eventAbbreviation,
    isPrimary: template.isPrimary,
    bowlerQuantity: quantity,
    gamesPerEvent: template.isPrimary ? template.gamesPerEvent : null,
    eventScope: "tournament",
    tournamentEventCalculationTypeID: calculationTypeId,
    maxSharedPlayers: Number(getRecommendedPlacementValue(quantity)),
    fees: [],
  };

  if (isAllEventTemplate(template)) {
    payload.allEventConfig = {
      primaryEventIds: (events || [])
        .filter((event) => event.isPrimary && !event.isTemplate)
        .map((event) => event.id),
      secondaryRuleType: "first_attempt",
      isValid: true,
    };
  }

  return payload;
};
